import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { ShieldCheck, ExternalLink, ChevronDown } from 'lucide-react';
import SectionBand from '@/components/ui/SectionBand';
import { supabase } from '@/lib/supabase';
import {
  BENEFIT_LABELS,
  FAYE_SUPPORT,
  INSURANCE_STATUS,
  fayePlanDocumentUrl,
  insuranceCardState,
} from '../../../supabase/functions/_shared/trip-insurance.js';
import { FAYE_RETAILER_DISCLOSURE, FAYE_TRIP_COVERAGE_DISCLOSURE } from '../../../supabase/functions/_shared/faye-pricing.js';

/** Faye's wordmark, from withfaye.com's header; follows the text color. */
function FayeLogo({ className }) {
  return (
    <svg viewBox="0 0 149 75" className={className} role="img" aria-label="Faye" fill="currentColor">
      <path d="M120.417 40.0757L148.75 31.4392C147.917 20.9089 140.644 13.8635 130.19 13.8635C118.674 13.8635 110.72 22.5 110.72 34.1664C110.72 45.9847 118.902 54.5453 131.932 54.5453C136.553 54.5453 141.174 53.4848 145.72 50.9846V42.9544C141.477 45.8332 136.705 47.121 132.386 47.121C126.174 47.121 122.159 44.3938 120.417 40.0757ZM130.19 21.2878C134.356 21.2878 137.387 23.2575 138.977 26.9696L119.432 32.9545C119.811 26.0606 123.977 21.2878 130.19 21.2878Z" />
      <path d="M9.16658 23.8201V8.40909H33.5605V0H0V53.6363H9.16658V31.7104C15.53 32.9982 21.7423 32.7274 28.0301 29.6972V21.2122C21.7423 24.2424 15.4544 24.8049 9.16658 23.8201Z" />
      <path d="M64.4922 42.9544V28.3334C64.4922 18.9394 58.7347 13.8635 48.5074 13.8635C42.674 13.8635 37.977 15.5303 34.1893 18.1816V26.6666C37.9014 23.3334 42.8255 21.2121 47.977 21.2121C52.9014 21.2121 55.7042 23.1816 55.7042 26.7422C55.7042 28.8635 54.7193 29.0909 52.977 29.318L45.0983 30.3787C37.4466 31.3636 31.6133 34.9242 31.6133 42.5755C31.6133 50.1515 37.2952 54.5453 44.8709 54.5453C50.1741 54.5453 54.1133 52.4999 56.5376 48.6363C56.6891 50.1515 57.068 51.9695 57.5983 53.6363H66.3104C65.0226 50.1515 64.4922 46.515 64.4922 42.9544ZM55.7801 38.3333C55.7801 44.6968 51.2346 47.1969 46.8406 47.1969C43.1285 47.1969 40.4013 45.3787 40.4013 41.9695C40.4013 38.8634 42.674 37.0455 46.8406 36.5151L52.2195 35.7576C53.8862 35.5302 55.0226 35.2272 55.7801 34.6212V38.3333Z" />
      <path d="M96.6135 36.0605C96.6135 42.9544 93.3559 46.9695 87.9014 46.9695C83.5833 46.9695 80.0983 44.4696 80.0983 38.1059V14.7725H71.3862V39.3182C71.3862 49.3937 77.8259 54.5452 85.6287 54.5452C90.4772 54.5452 94.4167 52.5757 96.8409 48.7119V52.4242C96.8409 62.8787 90.2501 67.4242 82.144 67.4242C76.3106 67.4242 71.0717 65.0755 68.3444 59.8485H59.6323C63.0414 70.1514 72.2196 74.9999 82.144 74.9999C94.1893 74.9999 105.25 67.7272 105.25 52.6514V14.7725H96.6135V36.0605Z" />
    </svg>
  );
}
FayeLogo.propTypes = { className: PropTypes.string };

const money = (cents) => `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const shortDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (!m) return null;
  return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

const CUSTOMER_TONE = {
  pending: 'bg-white/[0.06] text-white/70 border-white/10',
  active: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/25',
  refund: 'bg-sky-500/10 text-sky-300 border-sky-500/25',
  closed: 'bg-white/[0.04] text-white/50 border-white/10',
};

const OPS_LABEL = {
  pending: 'Not issued yet',
  issued: 'Issued',
  refund_due: 'REFUND DUE',
  refunded: 'Refunded',
  needs_cancellation: 'CANCEL WITH FAYE',
  cancelled: 'Cancelled',
};
const OPS_TONE = {
  pending: 'bg-amber-500/10 text-amber-300 border-amber-500/25',
  issued: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/25',
  refund_due: 'bg-rose-500/10 text-rose-300 border-rose-500/25',
  refunded: 'bg-white/[0.04] text-white/50 border-white/10',
  needs_cancellation: 'bg-rose-500/10 text-rose-300 border-rose-500/25',
  cancelled: 'bg-white/[0.04] text-white/50 border-white/10',
};
const OPS_BUTTON = 'h-8 px-3 rounded-full bg-white/[0.06] border border-white/10 text-white/70 text-[12.5px] font-normal hover:bg-white/[0.1] transition-colors disabled:opacity-50';
const OPS_PRIMARY = 'h-8 px-3 rounded-full bg-white text-black text-[12.5px] font-medium hover:bg-white/90 transition-colors disabled:opacity-50';
const OPS_INPUT = 'h-8 rounded-lg bg-black/30 border border-white/15 px-2.5 text-[12.5px] text-white placeholder:text-white/30 focus:outline-none focus:border-emerald-400/50';

/**
 * Ops only: where the policy stands with Faye, and the moves that change it
 * (server: POST /api/admin/trip-insurance-status). Shows what buying it in
 * Faye's portal needs: travelers, dates, state, trip cost and whether the
 * price was an estimate.
 */
/** `readOnly`: the concierge sees the policy's ops status and figures, never its controls. */
function PolicyOpsStrip({ policy, bookingId, onPolicyChange, readOnly = false }) {
  const [policyNumber, setPolicyNumber] = useState('');
  const [fayePrice, setFayePrice] = useState('');
  /** The settlement's amount when it worked one out (the premium's share of
   *  what was paid), else empty: ops enters what actually went back. */
  const owedRefund = () => (policy.status === INSURANCE_STATUS.REFUND_DUE && policy.refundKnown
    ? (policy.refundCents / 100).toFixed(2)
    : '');
  const [refundAmount, setRefundAmount] = useState(owedRefund);
  // A move into refund_due while the strip is open brings its amount in.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setRefundAmount(owedRefund()); }, [policy.status, policy.refundCents, policy.refundKnown]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const send = (status) => {
    const body = { booking_id: bookingId, status };
    if (policyNumber.trim()) body.policy_number = policyNumber.trim();
    const price = Math.round(Number(fayePrice) * 100);
    if (fayePrice.trim() && Number.isFinite(price)) body.faye_premium_cents = price;
    if (status === INSURANCE_STATUS.REFUNDED) {
      const refund = Math.round(Number(refundAmount) * 100);
      if (!refundAmount.trim() || !Number.isFinite(refund) || refund < 0) {
        setError('Enter the amount refunded to the card.');
        return undefined;
      }
      body.refund_cents = refund;
      // Premium refunds go back to the card, never as Defera Credit (Terms 2.17).
      body.refund_method = 'card';
    }
    return post(body);
  };

  /** A flight change lowered the premium; ops refunded the card. */
  const markAdjustmentRefunded = (index) => post({ booking_id: bookingId, adjustment_refunded: index });

  const post = async (body) => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/admin/trip-insurance-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || 'Could not update the policy.');
      setPolicyNumber('');
      setFayePrice('');
      onPolicyChange?.(data.policy);
    } catch (e) {
      setError(e.message || 'Could not update the policy.');
    } finally {
      setSaving(false);
    }
  };

  const quote = policy.quote || {};
  const notes = [...(quote.result?.notes || quote.notes || []), ...(quote.result?.warnings || quote.warnings || [])];
  // The add after booking is the log's first entry, not a re-price; the
  // indexes stay the log's own, which the refund marker posts back.
  const addedEntry = policy.adjustments.find((a) => a?.source === 'added_after_booking') || null;
  const repricings = policy.adjustments.map((a, i) => ({ a, i })).filter(({ a }) => a?.source !== 'added_after_booking');
  const isEstimate = quote.result ? quote.result.exact === false : quote.exact === false;
  const travelers = policy.travelers
    .map((t) => [t?.name, Number.isFinite(Number(t?.age)) ? `${t.age}` : null].filter(Boolean).join(', '))
    .filter(Boolean);

  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 space-y-2.5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-white/40 text-[10px] uppercase tracking-[0.14em] font-normal">Ops · Faye policy</span>
          <span className={`px-2 py-0.5 rounded-full border text-[11px] font-medium ${OPS_TONE[policy.status] || OPS_TONE.pending}`}>
            {OPS_LABEL[policy.status] || policy.status}
          </span>
        </div>
        {policy.policyNumber && (
          <span className="text-white/45 text-[11.5px] font-normal tabular-nums">#{policy.policyNumber}</span>
        )}
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px] font-normal">
        <div><dt className="text-white/40">Travelers</dt><dd className="text-white/80">{travelers.join(' · ') || '—'}</dd></div>
        <div><dt className="text-white/40">Trip</dt><dd className="text-white/80">{[shortDate(policy.departDate), shortDate(policy.returnDate)].filter(Boolean).join(' – ') || '—'}{policy.international ? ' · abroad' : ''}</dd></div>
        <div><dt className="text-white/40">Insured trip cost</dt><dd className="text-white/80 tabular-nums">{policy.insuredCostCents ? money(policy.insuredCostCents) : '—'}</dd></div>
        <div><dt className="text-white/40">Home state</dt><dd className="text-white/80">{policy.residenceState || '—'}</dd></div>
        <div><dt className="text-white/40">Customer pays</dt><dd className="text-white/80 tabular-nums">{money(policy.premiumCents)}</dd></div>
        <div><dt className="text-white/40">Faye charged</dt><dd className="text-white/80 tabular-nums">{policy.fayePremiumCents ? money(policy.fayePremiumCents) : '—'}</dd></div>
      </dl>
      {isEstimate && (
        <p className="text-amber-300/80 text-[11.5px] font-normal leading-snug">
          Estimated price: check it in Faye&apos;s portal before buying.{notes.length ? ` ${notes.join('; ')}.` : ''}
        </p>
      )}

      {addedEntry && (
        <div className="space-y-1">
          <p className="text-white/40 text-[10px] uppercase tracking-[0.14em] font-normal">Added after booking</p>
          <p className="text-white/70 text-[12px] font-normal tabular-nums">
            {shortDate(addedEntry.at)} · {money(Number(addedEntry.to_premium_cents) || 0)}{' '}
            {addedEntry.mode === 'upfront'
              ? `charged upfront (${addedEntry.processor === 'nmi' ? 'NMI' : 'Stripe'}, outside the plan)`
              : `spread across ${addedEntry.payments} remaining payment${addedEntry.payments === 1 ? '' : 's'}`}
          </p>
        </div>
      )}

      {repricings.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-white/40 text-[10px] uppercase tracking-[0.14em] font-normal">Re-priced after flight changes</p>
          {repricings.map(({ a, i }) => (
            <div key={`${a.at}-${i}`} className="flex items-center justify-between gap-3 flex-wrap text-[12px] font-normal">
              <span className="text-white/70 tabular-nums">
                {shortDate(a.at)} · {money(Number(a.from_premium_cents) || 0)} → {money(Number(a.to_premium_cents) || 0)}
              </span>
              {a.charged_cents > 0 ? (
                <span className="text-white/50">{money(a.charged_cents)} charged with the change</span>
              ) : a.owed_back_cents > 0 ? (
                a.refunded_at ? (
                  <span className="text-white/50">{money(a.owed_back_cents)} refunded {shortDate(a.refunded_at)}</span>
                ) : readOnly ? (
                  <span className="text-white/50">{money(a.owed_back_cents)} owed back to the card</span>
                ) : (
                  <button type="button" onClick={() => markAdjustmentRefunded(i)} disabled={saving} className={OPS_BUTTON}>
                    Refunded {money(a.owed_back_cents)} to the card
                  </button>
                )
              ) : a.absorbed_cents > 0 ? (
                <span className="text-white/50">{money(a.absorbed_cents)} absorbed</span>
              ) : (
                <span className="text-white/40">No price change</span>
              )}
            </div>
          ))}
        </div>
      )}

      {!readOnly && (policy.status === INSURANCE_STATUS.PENDING || policy.status === INSURANCE_STATUS.ISSUED) && (
        <div className="flex items-center gap-2 flex-wrap">
          <input
            id={`faye-policy-number-${bookingId}`}
            type="text"
            value={policyNumber}
            onChange={(e) => setPolicyNumber(e.target.value)}
            placeholder={policy.policyNumber ? 'New policy #' : 'Faye policy #'}
            aria-label="Faye policy number"
            className={`${OPS_INPUT} w-40`}
          />
          <input
            id={`faye-price-${bookingId}`}
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={fayePrice}
            onChange={(e) => setFayePrice(e.target.value)}
            placeholder="Faye price $"
            aria-label="What Faye charged, in dollars"
            className={`${OPS_INPUT} w-32`}
          />
          {policy.status === INSURANCE_STATUS.PENDING ? (
            <>
              <button type="button" onClick={() => send(INSURANCE_STATUS.ISSUED)} disabled={saving} className={OPS_PRIMARY}>Mark issued</button>
              <button type="button" onClick={() => send(INSURANCE_STATUS.REFUND_DUE)} disabled={saving} className={OPS_BUTTON}>Refund due</button>
              <button type="button" onClick={() => send(INSURANCE_STATUS.CANCELLED)} disabled={saving} className={OPS_BUTTON}>Not needed</button>
            </>
          ) : (
            <>
              <button type="button" onClick={() => send(INSURANCE_STATUS.ISSUED)} disabled={saving} className={OPS_PRIMARY}>Save</button>
              <button type="button" onClick={() => send(INSURANCE_STATUS.NEEDS_CANCELLATION)} disabled={saving} className={OPS_BUTTON}>Cancel with Faye</button>
            </>
          )}
        </div>
      )}
      {!readOnly && policy.status === INSURANCE_STATUS.REFUND_DUE && (
        <div className="flex items-center gap-2 flex-wrap">
          <input
            id={`faye-refund-${bookingId}`}
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={refundAmount}
            onChange={(e) => setRefundAmount(e.target.value)}
            aria-label="Amount refunded, in dollars"
            className={`${OPS_INPUT} w-32`}
          />
          <span className="text-white/50 text-[12.5px]">to the card</span>
          <button type="button" onClick={() => send(INSURANCE_STATUS.REFUNDED)} disabled={saving} className={OPS_PRIMARY}>Mark refunded</button>
        </div>
      )}
      {!readOnly && policy.status === INSURANCE_STATUS.NEEDS_CANCELLATION && (
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={() => send(INSURANCE_STATUS.REFUND_DUE)} disabled={saving} className={OPS_PRIMARY}>Faye refunded: refund due</button>
          <button type="button" onClick={() => send(INSURANCE_STATUS.CANCELLED)} disabled={saving} className={OPS_BUTTON}>Cancelled, no refund</button>
          <button type="button" onClick={() => send(INSURANCE_STATUS.ISSUED)} disabled={saving} className={OPS_BUTTON}>Keep it</button>
        </div>
      )}
      {error && <p className="text-rose-300/90 text-[11.5px] font-normal" role="alert">{error}</p>}
      <p className="text-white/35 text-[11px] font-normal leading-snug">
        {policy.status === INSURANCE_STATUS.PENDING
          ? 'Buy it in Faye’s advisor portal once the trip is ticketed, at least 3 days before departure, then enter the policy number.'
          : policy.status === INSURANCE_STATUS.NEEDS_CANCELLATION
            ? 'The trip ended after the policy was issued. Faye refunds a cancellation within 14 days of issue; mark it refund due once Faye has.'
            : 'Customers see this policy as included until their trip is paid in full, then its number. Never confirm the purchase to them.'}
      </p>
    </div>
  );
}
PolicyOpsStrip.propTypes = {
  policy: PropTypes.object.isRequired,
  bookingId: PropTypes.string,
  onPolicyChange: PropTypes.func,
  readOnly: PropTypes.bool,
};

/**
 * The Travel Insurance card on a trip: the Faye plan the customer bought,
 * what it covers, where it stands, and how to reach Faye. Renders nothing
 * for a trip without insurance.
 */
export default function TripInsuranceCard({ policy, opsPolicy = null, tripActive = true, premiumCents = 0, addedAfterBooking = null, bookingId, isAdmin = false, showOps = false, onPolicyChange }) {
  const [showAll, setShowAll] = useState(false);
  // What the payments carry is the checkout premium (the booking's); a flight
  // change re-prices the policy, and the difference is settled on its own.
  const cents = premiumCents || policy?.premiumCents || 0;
  const repricedCents = policy?.premiumCents && policy.premiumCents !== cents ? policy.premiumCents : null;
  if (!cents) return null;
  const state = insuranceCardState(policy, { tripActive });
  const benefits = policy
    ? Object.entries(BENEFIT_LABELS).filter(([key]) => typeof policy.benefits[key] === 'string' && policy.benefits[key])
    : [];
  const docUrl = policy ? fayePlanDocumentUrl(policy.plan, policy.residenceState, { international: policy.international }) : null;
  const dates = policy ? [shortDate(policy.departDate), shortDate(policy.returnDate)].filter(Boolean).join(' – ') : '';
  const travelerCount = policy?.travelers?.length || 0;

  return (
    <motion.div
      className="bg-gradient-to-b from-brand-900/55 via-brand-900/28 to-brand-900/12 backdrop-blur-sm border border-brand-800/30 rounded-3xl overflow-hidden"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.25 }}
    >
      <SectionBand
        icon={ShieldCheck}
        iconColor="text-brand-400"
        title="Travel Insurance"
        rightSlot={<FayeLogo className="h-4 w-auto text-white/70 flex-shrink-0" />}
      />
      <div className="p-6 space-y-4">
        {(isAdmin || showOps) && (opsPolicy || policy) && <PolicyOpsStrip policy={opsPolicy || policy} bookingId={bookingId} onPolicyChange={onPolicyChange} readOnly={!isAdmin} />}

        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h4 className="text-white font-light text-lg mb-1">{policy?.label || 'Travel insurance by Faye'}</h4>
            <p className="text-sm text-white/60 font-normal">
              {state?.detail || 'Your policy details will show here.'}
            </p>
          </div>
          {state && (
            <span className={`px-2.5 py-0.5 rounded-full border text-[11px] font-medium whitespace-nowrap ${CUSTOMER_TONE[state.tone]}`}>
              {state.label}
            </span>
          )}
        </div>

        {policy && (
          <div className="grid grid-cols-2 gap-4 text-sm">
            {policy.policyNumber && policy.status !== INSURANCE_STATUS.REFUNDED ? (
              <div>
                <p className="text-white/60 font-normal mb-1">Policy number</p>
                <p className="text-white font-normal tabular-nums select-all">{policy.policyNumber}</p>
              </div>
            ) : (
              <div>
                <p className="text-white/60 font-normal mb-1">Covers</p>
                <p className="text-white font-normal">{travelerCount === 1 ? '1 traveler' : `${travelerCount} travelers`}</p>
              </div>
            )}
            <div>
              <p className="text-white/60 font-normal mb-1">Trip dates</p>
              <p className="text-white font-normal">{dates || '—'}</p>
            </div>
          </div>
        )}

        {policy && policy.highlights.length > 0 && (
          <div className="pt-3 border-t border-white/10">
            <p className="text-white/60 font-normal text-sm mb-2">What’s included</p>
            <ul className="space-y-1.5">
              {policy.highlights.map((line) => (
                <li key={line} className="relative pl-3.5 text-sm text-white/75 font-normal before:content-[''] before:absolute before:left-0 before:top-[0.6em] before:w-1 before:h-1 before:rounded-full before:bg-white/35">
                  {line}
                </li>
              ))}
            </ul>
            {benefits.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={() => setShowAll((v) => !v)}
                  aria-expanded={showAll}
                  className="mt-3 inline-flex items-center gap-1 text-[12.5px] text-brand-300 font-normal hover:text-brand-200"
                >
                  {showAll ? 'Hide full coverage' : 'Full coverage'}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showAll ? 'rotate-180' : ''}`} />
                </button>
                {showAll && (
                  <dl className="mt-2 space-y-1.5 text-[13px] font-normal">
                    {benefits.map(([key, label]) => (
                      <div key={key} className="flex flex-col sm:flex-row sm:justify-between sm:gap-4">
                        <dt className="text-white/55 sm:flex-shrink-0">{label}</dt>
                        <dd className="text-white/80 sm:text-right">{policy.benefits[key]}</dd>
                      </div>
                    ))}
                    {policy.excludedReasons.length > 0 && (
                      <p className="pt-1 text-white/45 text-[12px]">
                        Not a covered reason to cancel or interrupt your trip on this plan: {policy.excludedReasons.join('; ').toLowerCase()}.
                      </p>
                    )}
                  </dl>
                )}
              </>
            )}
          </div>
        )}

        <div className="flex items-center justify-between gap-4 pt-3 border-t border-white/10">
          <div className="min-w-0">
            <p className="text-white/60 font-normal text-sm mb-1">Insurance cost</p>
            <p className="text-white/45 font-normal text-xs">
              {addedAfterBooking?.mode === 'upfront'
                ? `Paid ${shortDate(addedAfterBooking.at) ? `on ${shortDate(addedAfterBooking.at)}` : 'upfront'}, with no fees added`
                : addedAfterBooking?.mode === 'spread'
                  ? 'Added after booking and spread across your remaining payments, with no fees added'
                  : 'Spread across your payments after the deposit, with no fees added'}
            </p>
          </div>
          <p className="text-white font-light text-lg tabular-nums whitespace-nowrap">{money(cents)}</p>
        </div>
        {repricedCents != null && (
          <p className="-mt-1 text-white/45 font-normal text-xs">
            Re-priced to {money(repricedCents)} for your new flight.{' '}
            {repricedCents > cents
              ? 'The difference was charged with your flight change.'
              : 'The difference goes back to your card.'}
          </p>
        )}

        <div className="pt-3 border-t border-white/10 space-y-2 text-[13px] font-normal">
          <p className="text-white/60">
            Questions about your coverage go to Faye: <span className="text-white/85 select-all">{FAYE_SUPPORT.email}</span>
            {' · '}<span className="text-white/85 select-all tabular-nums whitespace-nowrap">{FAYE_SUPPORT.phoneUs}</span>
            {' '}(<span className="select-all tabular-nums whitespace-nowrap">{FAYE_SUPPORT.phoneIntl}</span> outside the US).
            File claims in the Faye app.
          </p>
          {docUrl && (
            <a href={docUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-brand-300 hover:text-brand-200">
              Plan document <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          <details className="text-white/45 text-[11.5px] leading-relaxed">
            <summary className="cursor-pointer text-white/55 hover:text-white/75">Disclosures</summary>
            <p className="mt-2">{FAYE_TRIP_COVERAGE_DISCLOSURE}</p>
            <p className="mt-2">{FAYE_RETAILER_DISCLOSURE}</p>
          </details>
        </div>
      </div>
    </motion.div>
  );
}

TripInsuranceCard.propTypes = {
  policy: PropTypes.object,
  opsPolicy: PropTypes.object,
  tripActive: PropTypes.bool,
  premiumCents: PropTypes.number,
  /** flight_data._trip_insurance_added: how a plan added after booking is paid. */
  addedAfterBooking: PropTypes.object,
  bookingId: PropTypes.string,
  isAdmin: PropTypes.bool,
  /** Staff who may read the ops strip without its controls (the concierge). */
  showOps: PropTypes.bool,
  onPolicyChange: PropTypes.func,
};
